import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import Header from "./components/Header";
import Sidebar from "./components/Sidebar";
import Login from "./pages/Login"; 
import Teams from "./pages/Teams";
import Station from "./pages/Station";
import Ergebnisse from "./pages/Ergebnisse";
import AgeBonus from "./pages/AgeBonus";
import Wertung from "./pages/Wertung";
import Exporte from "./pages/Exporte";
import Freischalten from "./pages/Freischalten";
import Systemverwaltung from "./pages/Systemverwaltung";
import RankingPage from "./pages/RankingPage";
import LiveErgebnisse from "./pages/LiveErgebnisse";
import ProtectedRoute from "./components/ProtectedRoute"; 
import { useAuth } from "./context/AuthContext"; 
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import FullScreenLoader from "./components/ui/FullScreenLoader";
import Unauthorized from "./pages/Unauthorized";
import Preisverleihung from "./pages/Preisverleihung";
import PreiseVerwalten from "./pages/PreiseVerwalten";
import MeinTeam from "./pages/MeinTeam";
import ImportTeams from "./pages/ImportTeams";
import ImportStationen from "./pages/ImportStationen";
import Lageplan from "./pages/Lageplan";
import Nutzerverwaltung from "./pages/Nutzerverwaltung";
import Quiz from "./pages/Quiz";
import QuizVerwaltung from "./pages/QuizVerwaltung";
import WarteschlangeAnzeige from "./components/WarteschlangeAnzeige";
import { starteAutoSenden } from "./utils/eintragsWarteschlange";
import { toast } from "react-toastify";

export default function App() {
  const { user, loading } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Offline zwischengespeicherte Ergebnisse nachsenden, solange jemand
  // angemeldet ist – unabhaengig davon, auf welcher Seite er gerade steht.
  const darfEintragen = user && (user.role === "admin" || user.role === "betreuer");
  // Quiz-Tablet: Vollbild ohne Seitenleiste und Kopfzeile – Teilnehmer sollen
  // nirgendwohin navigieren koennen (die Rolle darf ohnehin nur das Quiz).
  const kiosk = user?.role === "quiz";
  useEffect(() => {
    if (!darfEintragen) return undefined;
    return starteAutoSenden(process.env.REACT_APP_BACKEND_URL, (anzahl) =>
      toast.success(
        `${anzahl} zwischengespeicherte${anzahl === 1 ? "s Ergebnis" : " Ergebnisse"} nachgesendet`,
        { toastId: "warteschlange-gesendet" }
      )
    );
  }, [darfEintragen]);

  // 1. Initial Loading Screen: Wait for AuthContext to finish loading.
  if (loading) {
    return <FullScreenLoader text="Starte Anwendung..." />;
  }

  // 2. Main App Content: Render after loading is complete.
  return (
    <>
    <Router>
      <div className="min-h-screen flex bg-secondary-950 text-white">
        {user && !kiosk && (
          <Sidebar
            user={user}
            open={sidebarOpen}
            onClose={() => setSidebarOpen(false)}
          />
        )}

        <div className="flex-1 flex flex-col w-full">
          {user && !kiosk && <Header onOpenSidebar={() => setSidebarOpen(true)} />}

          <main className={`flex-1 overflow-auto bg-secondary-950 ${kiosk ? "p-4" : "p-4 sm:p-6"}`}>
            <Routes>
              {/* Login Route: Redirect to home if logged in, otherwise show login page. */}
              <Route path="/login" element={user ? <Navigate to="/" replace /> : <Login />} />
              <Route path="/unauthorized" element={<Unauthorized />} />

              {/* Home Route: Handle the initial redirection based on login status. */}
              <Route 
                path="/" 
                element={
                  user 
                    ? (user.role === 'admin'
                        ? <Navigate to="/teams" replace />
                        : user.role === "quiz"
                          ? <Navigate to="/quiz" replace />
                          : <Navigate to="/station" replace />)
                    : <Navigate to="/login" replace />
                } 
              />

              {/* Protected Routes with Role Checks */}
              <Route
                path="/teams"
                element={
                  <ProtectedRoute roles={["admin"]}>
                    <Teams />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/station"
                element={
                  <ProtectedRoute roles={["admin", "betreuer"]}>
                    <Station />
                  </ProtectedRoute>
                }
              />
              <Route path="/ergebnisse" element={<Ergebnisse />} />

              {/* Persoenliche Team-Ansicht: bewusst OHNE ProtectedRoute.
                  Der Zugang laeuft ueber den share_token in der URL, den nur
                  das jeweilige Team kennt – eine Anmeldung waere fuer die
                  Teilnehmer eine Huerde ohne Sicherheitsgewinn. Das Backend
                  gibt hier ausschliesslich die Daten dieses einen Teams heraus,
                  ohne Rang und ohne Bezug zu anderen Teams. */}
              <Route path="/team/:token" element={<MeinTeam />} />
              <Route
                path="/import"
                element={
                  <ProtectedRoute roles={["admin"]}>
                    <ImportTeams />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/import-stationen"
                element={
                  <ProtectedRoute roles={["admin"]}>
                    <ImportStationen />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/agebonus"
                element={
                  <ProtectedRoute roles={["admin"]}>
                    <AgeBonus />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/wertung"
                element={
                  <ProtectedRoute roles={["admin"]}>
                    <Wertung />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/exporte"
                element={
                  <ProtectedRoute roles={["admin"]}>
                    <Exporte />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/freischalten"
                element={
                  <ProtectedRoute roles={["admin"]}>
                    <Freischalten />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/systemverwaltung"
                element={
                  <ProtectedRoute roles={["admin"]}>
                    <Systemverwaltung />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/lageplan"
                element={
                  <ProtectedRoute roles={["admin"]}>
                    <Lageplan />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/liveergebnisse"
                element={
                  <ProtectedRoute roles={["admin"]}>
                    <LiveErgebnisse />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/preisverleihung"
                element={
                  <ProtectedRoute roles={["admin"]}>
                    <Preisverleihung />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/preise-verwalten"
                element={
                  <ProtectedRoute roles={["admin"]}>
                    <PreiseVerwalten />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nutzer"
                element={
                  <ProtectedRoute roles={["admin"]}>
                    <Nutzerverwaltung />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/quiz"
                element={
                  <ProtectedRoute roles={["quiz", "admin"]}>
                    <Quiz />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/quiz-verwaltung"
                element={
                  <ProtectedRoute roles={["admin"]}>
                    <QuizVerwaltung />
                  </ProtectedRoute>
                }
              />
              <Route path="/ranking" element={kiosk ? <Navigate to="/quiz" replace /> : <RankingPage />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </main>

          {user && !kiosk && (
            <footer className="p-3 text-sm text-gray-400 border-t border-secondary-800">
              © {new Date().getFullYear()} Kirtagames
            </footer>
          )}
        </div>
      </div>
    </Router>
    
    {/* Nicht gesendete Ergebnisse – auch auf der Login-Seite sichtbar, damit
        nach abgelaufener Sitzung niemand sie uebersieht. */}
    <WarteschlangeAnzeige />

    {/* ToastContainer global */}
      <ToastContainer position="top-right" autoClose={3000} />
    </>
  );
}